import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ICampaignOverview } from '../../../model/briefing/overview/ICampaignOverview';
import EmptyState from '../../../shared/components/placeholder/EmptyState';
import RocketModal from '../../../shared/components/modal/RocketModal';
import { useSearchQuery } from '../../../shared/hooks/useSearchQuery';
import { OffersIcon } from '../../../shared/Icons';
import { ErrorToast } from '../../../utils/toasters';
import { OffersCardItem } from './components/OffersCardItem';
import { OffersFilter } from './components/OffersFilter';
import { OffersMobileTabs } from './components/OffersMobileTabs';
import { IOfferManagementFilter } from './dto/IOfferManagementFilter';
import { InfluencerOfferModal } from './modal/offer-modal/InfluencerOfferModal';
import { ManagementOfferService } from './service';
import { InfluencerContractModal } from './modal/contract-modal/InfluencerContractModal';
import { ContractCardItem } from './components/ContractCardItem';
import { ENotificationType } from '../../../model/campaign/Campaign';
import { OfferCardItemSkeleton } from './components/OfferCardItemSkeleton';

const SKELETON_COUNT = 1;

interface ILocationProps {
    selected: 'to-do' | 'in-progress';
}

export function OfferManagement() {
    const { state } = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [todoOffers, setTodoOffers] = useState<ICampaignOverview[]>([]);
    const [inProgressOffers, setInProgressOffers] = useState<ICampaignOverview[]>([]);
    const [newDeclined, setNewDeclined] = useState(false);
    const [tab, selectTab] = useState(((state as ILocationProps)?.selected as string) ?? 'to-do');
    const [showOfferModal, setShowOfferModal] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [updateList, setUpdateList] = useState(false);
    const [isRocketShown, setIsRocketShown] = useState(false);
    const [campaignId, setCampaignId] = useState('');
    const [campaignTitle, setCampaignTitle] = useState('');
    const [offerFilterParams, setOfferFilterParams] = useState<IOfferManagementFilter>({
        name: '',
        status: '',
        isCharity: false,
        sortBy: 'date',
        sortType: 'DESC',
        isContract: false,
    });
    const [isLoading, setIsLoading] = useState(true);
    const lastQueryKeyRef = useRef<string>();
    const campaignQueryId = useSearchQuery('campaignId');
    const notificationTypeQuery = useSearchQuery('notificationType');
    const [countOffer, setCountOffer] = useState({
        toDo: 0,
        inProgress: 0,
    });

    useEffect(() => {
        // Skelet samo kad se promijeni filter; updateList (nakon modala ili otvaranja nove ponude) osvježava tiho
        const queryKey = JSON.stringify(offerFilterParams);
        if (lastQueryKeyRef.current !== queryKey) {
            lastQueryKeyRef.current = queryKey;
            setIsLoading(true);
        }

        let isCurrent = true;
        ManagementOfferService.getOfferList(offerFilterParams)
            .then(response => {
                if (!isCurrent) return;
                setTodoOffers(response.todoOffers.campaigns);
                setCountOffer({
                    inProgress: response?.inProgressOffers?.campaignsCount ?? 0,
                    toDo: response?.todoOffers?.campaignsCount ?? 0,
                });
                setInProgressOffers(response.inProgressOffers.campaigns);
                setNewDeclined(response.declinedOffersDot);
            })
            .catch(err => ErrorToast(err))
            .finally(() => {
                if (isCurrent) setIsLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [offerFilterParams, updateList]);

    useEffect(() => {
        if (state) {
            navigate('.', { replace: true, state: undefined });
        }
    }, []);

    useEffect(() => {
        if (campaignQueryId) {
            if (
                notificationTypeQuery === ENotificationType.CONTRACT ||
                notificationTypeQuery === ENotificationType.CONTRACT_COMMENT
            ) {
                setShowContractModal(true);
            } else {
                setShowOfferModal(true);
            }
            setCampaignId(campaignQueryId);
        }
    }, [campaignQueryId, notificationTypeQuery]);

    const handleTab = (event: React.SyntheticEvent, newValue: string) => {
        selectTab(newValue);
    };

    const getCounter = (value: number) => (isLoading ? '–' : value);

    const mapData = (tabName: string) => {
        if (isLoading) {
            return Array.from({ length: SKELETON_COUNT }).map((_, i) => <OfferCardItemSkeleton key={i} />);
        }

        const isTabTodo = tabName === 'to-do';
        const dataArray = isTabTodo ? todoOffers : inProgressOffers;

        if (!dataArray.length) {
            return (
                <EmptyState
                    iconPath={isTabTodo ? '/icons/received.png' : '/icons/sent.png'}
                    addClass="mb-4 col-8 text-center mx-auto"
                    desc={
                        isTabTodo
                            ? t('influencer.offer_management.empty_to_do')
                            : t('influencer.offer_management.empty_in_progress')
                    }
                    btnText={t('influencer.dashboard.explore')}
                    navigateTo="/influencer/explore"
                />
            );
        }

        return dataArray.map(campaign =>
            !!campaign?.contracts?.length ? (
                <ContractCardItem
                    key={campaign?.id}
                    setCampaignId={setCampaignId}
                    setCampaignTitle={setCampaignTitle}
                    setShowModal={setShowContractModal}
                    isTodo={isTabTodo}
                    campaign={campaign}
                />
            ) : (
                <OffersCardItem
                    key={campaign?.id}
                    setCampaignId={setCampaignId}
                    setCampaignTitle={setCampaignTitle}
                    setShowModal={setShowOfferModal}
                    setUpdateList={isTabTodo ? setUpdateList : undefined}
                    isTodo={isTabTodo}
                    campaign={campaign}
                />
            )
        );
    };

    return (
        <>
            <RocketModal
                show={isRocketShown}
                closeModal={() => setIsRocketShown(false)}
                campaignTitle={campaignTitle}
            />
            <InfluencerOfferModal
                show={showOfferModal}
                setShowModal={setShowOfferModal}
                campaignId={campaignId}
                setIsRocketShown={setIsRocketShown}
                updateList={setUpdateList}
                isLongTerm={false}
            />
            <InfluencerContractModal
                show={showContractModal}
                setShowModal={setShowContractModal}
                campaignId={campaignId}
                setIsRocketShown={setIsRocketShown}
                updateList={setUpdateList}
            />
            <OffersFilter
                offerFilter={offerFilterParams}
                setOfferFilter={setOfferFilterParams}
                newDeclined={newDeclined}
                isFilterLoading={isLoading}
            />

            <Row className="d-none d-md-flex mb-5">
                <Col lg={6}>
                    <div className="d-flex align-items-center mb-4">
                        <OffersIcon width="30" height="30" color="#9ea5ab" />
                        <h4 className="mb-0 ml-2 text-uppercase">
                            {t('influencer.offer_management.to_do', { counter: getCounter(countOffer?.toDo) })}
                        </h4>
                    </div>
                    {mapData('to-do')}
                </Col>
                <Col lg={6}>
                    <div className="d-flex align-items-center mb-4">
                        <OffersIcon width="30" height="30" color="#9ea5ab" />
                        <h4 className="mb-0 ml-2 text-uppercase">
                            {t('influencer.offer_management.in_progress', {
                                counter: getCounter(countOffer?.inProgress),
                            })}
                        </h4>
                    </div>
                    {mapData('in-progress')}
                </Col>
            </Row>

            <div className="d-md-none">
                <OffersMobileTabs handleTab={handleTab} countOffer={countOffer} tab={tab} isLoading={isLoading} />
                <Row className="mt-2">{mapData(tab)}</Row>
            </div>
        </>
    );
}
